import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ChevronLeft,
  LogOut01,
  Settings01,
  VideoRecorder,
} from "@untitled-ui/icons-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@/components/forms/Button";
import DotButton from "@/components/helpers/DotButton";
import DropDownMenu from "@/components/helpers/DropDownMenu";
import DropDownMenuItem from "@/components/helpers/DropDownMenuItem";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { Media, User } from "@/types";
import { useTenant } from "@/user/services/tenant";
import { notify } from "@/utils";

import { useConversationChannel } from "../../hooks";
import {
  type MessageRequest,
  fetchConversationByIdQueryOptions,
  fetchConversationOfUser,
  upsertConversationInLists,
  useCreateCall,
  useFetchGroupDetails,
  useLeaveGroup,
  useSendMessageToConversation,
  useSendMessageToUser,
} from "../../services";
import type {
  Conversation,
  Message,
  MessengerMessageType,
  ReplyToMessage,
} from "../../types";
import MessengerHeader from "../MessengerHeader";
import ConversationImage from "../conversations/ConversationImage";
import ConversationUserImage from "../conversations/ConversationUserImage";
import GroupLeaveModal from "../group-settings/GroupLeaveModal";
import GroupSettingsPage from "../group-settings/GroupSettingsPage";
import { VoicePlaybackProvider } from "../voice/VoicePlaybackProvider";
import VoicePlayerBar from "../voice/VoicePlayerBar";
import ChatWaveBackground from "./ChatWaveBackground";
import ConversationContainer from "./ConversationContainer";
import MessageInput from "./MessageInput";

function fileMessageType(file?: Media): MessengerMessageType {
  if (!file) return "text";
  if (file.kind === "image") return "image";
  if (file.kind === "video") return "video";
  if (file.kind === "file") return "file";
  return "file";
}

function messageBody(
  content: string,
  file: Media | undefined,
  replyToId: number | undefined,
  type: MessengerMessageType | undefined,
): MessageRequest {
  return {
    type: type ?? fileMessageType(file),
    ...(content ? { content } : {}),
    ...(replyToId !== undefined ? { reply_to: replyToId } : {}),
    ...(file ? { file: file.uuid } : {}),
  };
}

type ConversationChatProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClose: () => void;
  showBackButton?: boolean;
  messagesClassName?: string;
  messageClassName?: string;
  messageInputClassName?: string;
  messageInputVariant?: "fullscreen" | "compact";
  bottomSlot?: React.ReactNode;
  showGroupMenu?: boolean;
  onOpenGroupSettings?: () => void;
  onConversationCreated?: (
    conversation: Conversation<true, true, true>,
  ) => void;
} & (
  | { conversation: Conversation<true, true, true>; user?: never }
  | { conversation?: never; user: User<true> }
);

export default function ConversationChat({
  open,
  setOpen,
  conversation,
  user,
  onClose,
  showBackButton = true,
  messagesClassName,
  messageClassName,
  messageInputClassName,
  messageInputVariant = "compact",
  bottomSlot,
  showGroupMenu = true,
  onOpenGroupSettings,
  onConversationCreated,
}: ConversationChatProps) {
  const { t } = useTranslation("messenger.user");
  const tenant = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sendMessageToConversation = useSendMessageToConversation();
  const sendMessageToUser = useSendMessageToUser();
  const [replyingTo, setReplyingTo] = useState<Message<true> | null>(null);
  const sendQueueRef = useRef<Promise<void>>(Promise.resolve());
  const [isGroupSettingsOpen, setIsGroupSettingsOpen] = useState(false);
  const [showLeaveGroupModal, setShowLeaveGroupModal] = useState(false);
  const [createdConversation, setCreatedConversation] =
    useState<Conversation<true, true, true>>();
  const resolvedConversation = conversation ?? createdConversation;
  const isDesktop = useMediaQuery("(min-width: 640px)");
  const createCall = useCreateCall(resolvedConversation?.id ?? 0);

  const group =
    resolvedConversation?.type === "group"
      ? resolvedConversation.group
      : undefined;
  const groupId = group?.id;
  const { data: groupDetails, isSuccess: isGroupDetailsReady } =
    useFetchGroupDetails(showGroupMenu ? groupId : undefined);
  const leaveGroup = useLeaveGroup(groupId ?? 0);

  const canUpdateGroup = showGroupMenu && groupDetails?.can_update === true;
  const showGroupLeave = showGroupMenu && groupDetails?.can_leave === true;
  const showGroupActions = canUpdateGroup || showGroupLeave;

  const { typingUsers, onTyping } = useConversationChannel(
    resolvedConversation?.id,
  );

  const sendToConversation = (
    activeConversation: Conversation<true, true, true>,
    content: string,
    file?: Media,
    replyToId?: number,
    replyTo?: ReplyToMessage,
    type?: MessengerMessageType,
  ) => {
    const payload = {
      id: activeConversation.id,
      data: messageBody(content, file, replyToId, type),
      ...(file && { file }),
      replyTo,
    };
    sendQueueRef.current = sendQueueRef.current
      .then(
        () =>
          new Promise<void>((resolve) => {
            sendMessageToConversation.mutate(payload, {
              onSettled: () => resolve(),
            });
          }),
      )
      .catch(() => {});
  };

  const handleSendMessage = (
    content: string,
    file?: Media,
    replyToId?: number,
    type?: MessengerMessageType,
  ) => {
    const replyTo: ReplyToMessage | undefined = replyingTo
      ? {
          id: replyingTo.id,
          type: replyingTo.file ? "file" : "text",
          content:
            replyingTo.type === "voice"
              ? t("Voice message")
              : (replyingTo.file?.file_name ?? replyingTo.content ?? ""),
          user: {
            first_name: replyingTo.user.first_name,
            last_name: replyingTo.user.last_name,
          },
        }
      : undefined;

    if (resolvedConversation) {
      sendToConversation(
        resolvedConversation,
        content,
        file,
        replyToId,
        replyTo,
        type,
      );
      return;
    }

    if (!user) return;

    sendMessageToUser.mutate(
      {
        id: user.id,
        data: messageBody(content, file, replyToId, type),
      },
      {
        onSuccess: async (message) => {
          const conversationId = message.messenger_conversation_id;
          let nextConversation: Conversation<true, true, true> | null =
            conversationId
              ? await queryClient.fetchQuery(
                  fetchConversationByIdQueryOptions(conversationId),
                )
              : null;

          if (nextConversation?.type !== "private") {
            nextConversation = await queryClient.fetchQuery(
              fetchConversationOfUser(user.id),
            );
          }

          if (nextConversation?.type === "private") {
            upsertConversationInLists(queryClient, {
              ...nextConversation,
              unread_messages_count: 0,
            });
            queryClient.setQueryData(
              fetchConversationByIdQueryOptions(nextConversation.id).queryKey,
              nextConversation,
            );
            setCreatedConversation(nextConversation);
            onConversationCreated?.(nextConversation);
          }
        },
      },
    );
  };

  const handleStartCall = () => {
    if (!resolvedConversation) return;
    createCall.mutate(undefined, {
      onSuccess: (data) => {
        const callUrl = `${window.location.origin}/meet/${data.code}`;
        handleSendMessage(callUrl);
        navigate({ to: "/meet/$code", params: { code: data.code } });
      },
    });
  };

  const canCall =
    import.meta.env.__HAS_MEET__ &&
    (tenant?.modules ?? []).includes("meet") &&
    !!resolvedConversation;

  if (
    !onOpenGroupSettings &&
    isGroupSettingsOpen &&
    resolvedConversation?.type === "group" &&
    groupId
  ) {
    return (
      <GroupSettingsPage
        groupId={groupId}
        fallbackName={group?.name}
        onBack={() => setIsGroupSettingsOpen(false)}
        onLeftGroup={onClose}
      />
    );
  }

  const isGroup = resolvedConversation?.type === "group";

  const handleLeaveGroup = () => {
    if (!groupId) return;
    leaveGroup.mutate(undefined, {
      onSuccess: () => {
        notify.success(t("Left group successfully"));
        setShowLeaveGroupModal(false);
        onClose();
      },
      onError: () => notify.error(t("Failed to leave group")),
    });
  };

  const isFullscreen = messageInputVariant === "fullscreen";
  const showTopVoiceBar = isFullscreen && isDesktop;

  const messageInput = (
    <MessageInput
      className={messageInputClassName}
      variant={messageInputVariant}
      onSendMessage={handleSendMessage}
      replyingTo={replyingTo}
      onCancelReply={() => setReplyingTo(null)}
      isLoading={
        sendMessageToConversation.isPending || sendMessageToUser.isPending
      }
      onTyping={onTyping}
    />
  );

  return (
    <VoicePlaybackProvider>
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        {showTopVoiceBar && <VoicePlayerBar />}
        <MessengerHeader open={open} setOpen={setOpen} showToggle={false}>
          <div className="flex w-full items-center gap-3">
            {showBackButton && (
              <Button
                color="tertiary-gray"
                square
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
              >
                <ChevronLeft className="size-5 text-gray-600 rtl:rotate-180" />
              </Button>
            )}
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {resolvedConversation ? (
                <>
                  <ConversationImage conversation={resolvedConversation} />
                  <div className="flex flex-col">
                    {resolvedConversation.type === "group" ? (
                      <span className="text-sm font-semibold text-gray-900">
                        {group?.name ?? t("Deleted group")}
                      </span>
                    ) : (
                      <>
                        <span className="text-sm font-semibold text-gray-900">
                          {resolvedConversation.user.first_name +
                            " " +
                            resolvedConversation.user.last_name}
                        </span>
                        <span className="text-sm text-gray-600">
                          {resolvedConversation.user.position?.name || "-"}
                        </span>
                      </>
                    )}
                  </div>
                </>
              ) : (
                user && (
                  <>
                    <ConversationUserImage user={user} />
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-gray-900">
                        {user.first_name + " " + user.last_name}
                      </span>
                      <span className="text-sm text-gray-600">
                        {user.position?.name || "-"}
                      </span>
                    </div>
                  </>
                )
              )}
            </div>
            <div className="flex items-center gap-1">
              {canCall && (
                <Button
                  color="tertiary-gray"
                  square
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartCall();
                  }}
                  isLoading={createCall.isPending}
                >
                  <VideoRecorder className="size-5 text-gray-600" />
                </Button>
              )}
              {isGroup && isGroupDetailsReady && showGroupActions && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="relative z-50"
                >
                  <DropDownMenu
                    button={<DotButton />}
                    size="sm"
                    className="z-50"
                  >
                    {canUpdateGroup && (
                      <DropDownMenuItem
                        startIcon={<Settings01 className="size-4" />}
                        onClick={() =>
                          onOpenGroupSettings
                            ? onOpenGroupSettings()
                            : setIsGroupSettingsOpen(true)
                        }
                      >
                        {t("Settings", { ns: "core.shared" })}
                      </DropDownMenuItem>
                    )}
                    {showGroupLeave && (
                      <DropDownMenuItem
                        variant="error"
                        startIcon={<LogOut01 className="size-4" />}
                        onClick={() => setShowLeaveGroupModal(true)}
                      >
                        {t("Leave group")}
                      </DropDownMenuItem>
                    )}
                  </DropDownMenu>
                </div>
              )}
            </div>
          </div>
        </MessengerHeader>
        {!showTopVoiceBar && <VoicePlayerBar compact />}
        {showGroupMenu && isGroup && groupId && (
          <GroupLeaveModal
            open={showLeaveGroupModal}
            onClose={() => setShowLeaveGroupModal(false)}
            onConfirm={handleLeaveGroup}
            isLoading={leaveGroup.isPending}
            isDelete={false}
          />
        )}
        {open && (
          <>
            <ChatWaveBackground>
              {resolvedConversation ? (
                <div className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col">
                  <ConversationContainer
                    conversation={resolvedConversation}
                    typingUsers={typingUsers}
                    onReply={setReplyingTo}
                    className={messagesClassName}
                    messageClassName={messageClassName}
                  />
                </div>
              ) : (
                <div className="flex-1" />
              )}
              {isFullscreen && !bottomSlot && messageInput}
            </ChatWaveBackground>
            {bottomSlot ?? (!isFullscreen && messageInput)}
          </>
        )}
      </div>
    </VoicePlaybackProvider>
  );
}
